import { useState, useEffect, useRef, CSSProperties } from 'react';
import cn from 'classnames';

import Button from 'components/button/Button';
import Collapse from 'components/collapse/Collapse';
import HtmlString from 'components/html-string/HtmlString';
import IconWarning from 'components/icon-warning/IconWarning';
import Heading from 'components/heading/Heading';
import Icon from 'components/icon/Icon';
import ContentContainer from 'components/content-container/ContentContainer';

import { IconWarning_Theme, IMessageGlobal } from 'source/NFR.types';

const MINIMUM_HEIGHT = 54;

const messageGlobalThemeMap: Record<number, string> = {
  [IconWarning_Theme.None]: '',
  [IconWarning_Theme.Red]: 'message-global--theme-red',
  [IconWarning_Theme.Blue]: 'message-global--theme-blue',
  [IconWarning_Theme.Gray]: ''
};

interface MessageGlobalProps extends IMessageGlobal {
  index: number;
  length: number;
  className?: string;
  setActiveIndex: (index: number) => void;
  activeIndex: number;
  setIsOpen: (isOpen: boolean) => void;
  isOpen: boolean;
}

const MessageGlobal: React.FC<MessageGlobalProps> = ({
  id,
  title,
  theme,
  message,
  labels,
  index,
  length,
  className,
  setActiveIndex,
  activeIndex,
  setIsOpen,
  isOpen
}) => {
  const [isMultiLined, setIsMultiLined] = useState<boolean | undefined>(undefined);
  const [isMounted, setIsMounted] = useState<boolean>(false);
  const [isExpandable, setIsExpandable] = useState<boolean>((!!message && !!message.text) || length > 1);
  const [wasPreviouslyActive, setWasPreviouslyActive] = useState<{
    wasActive: boolean | undefined;
    index: number | undefined;
  }>({
    wasActive: undefined,
    index: undefined
  });
  const [fade, setFade] = useState<'' | 'out' | 'in-next' | 'in-previous'>('');
  const [messageHeight, setMessageHeight] = useState<number | undefined>();
  const titleRef = useRef<HTMLHeadingElement>(null);
  const messageGlobalRef = useRef<HTMLDivElement>(null);
  const isActive = activeIndex === index;

  const toggle = (): void => {
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    // When animating to first message - lower height of message to make sure multiline-titled-messages disappear behind the first message
    if (!isMounted) return;
    const shouldAnimateToFirstMessage = !isOpen && isActive && index !== 0;

    if (shouldAnimateToFirstMessage && messageGlobalRef.current) {
      window.requestAnimationFrame(() => {
        if (messageGlobalRef.current) {
          messageGlobalRef.current.style.height = messageGlobalRef.current.offsetHeight + 'px';

          window.requestAnimationFrame(() => {
            if (messageGlobalRef.current) {
              messageGlobalRef.current.style.height = MINIMUM_HEIGHT + 'px';
            }
          });
        }
      });
    } else if (isOpen && messageGlobalRef.current?.style.height) {
      messageGlobalRef.current.style.height = '';
    }
  }, [isOpen, isMounted, isActive, index]);

  useEffect(() => {
    if (!titleRef.current) return;
    const multiLined = titleRef.current.offsetHeight > 32;
    if (multiLined) {
      setIsMultiLined(multiLined);
      setIsExpandable(true);
    }
  }, []);

  useEffect(() => {
    const becameInactive = wasPreviouslyActive.wasActive && !isActive;
    if (isMounted) {
      if (becameInactive) {
        setFade('out');
      }
      const becameActive = !wasPreviouslyActive.wasActive && isActive;
      if (becameActive) {
        const isNext = ((wasPreviouslyActive.index ?? 0) + 1) % length === index;
        if (isNext && isOpen) {
          setFade('in-next');
        } else {
          setFade('in-previous');
        }
      }
      if (becameActive || becameInactive) {
        setTimeout(() => setFade(''), 700);
      }
    }

    setWasPreviouslyActive({ wasActive: isActive, index: activeIndex });
    if (!isMounted) setIsMounted(true);
  }, [
    activeIndex,
    index,
    isActive,
    isMounted,
    isOpen,
    length,
    wasPreviouslyActive.index,
    wasPreviouslyActive.wasActive
  ]);

  const computeStyle = (): CSSProperties => {
    const width = 100 / length;
    const moveLeft = width * Math.min(index, 1);
    const initialOpacity = ((!fade && (isActive ? '1' : '0')) ?? undefined) as '0' | '1' | undefined;
    const initialVisibility = !fade && (isActive ? 'visible' : 'hidden');

    return {
      width: `${width}%`,
      marginLeft: `-${moveLeft}%`,
      opacity: initialOpacity ?? undefined,
      visibility: initialVisibility as 'visible' | 'hidden',
      zIndex: isActive ? 11 : undefined
    };
  };

  return (
    <div
      className={cn('message-global--container', messageGlobalThemeMap[theme], {
        'message-global--container--is-open': isOpen,
        'message-global--container--fade-out': fade === 'out' && isOpen,
        'message-global--container--fade-in-next': fade === 'in-next',
        'message-global--container--fade-in-previous': fade === 'in-previous' && isOpen,
        'message-global--container--fade-in-previous-slow': fade === 'in-previous' && !isOpen
      })}
      ref={messageGlobalRef}
      style={computeStyle()}
    >
      <ContentContainer className={cn('message-global', className)}>
        <Button
          onClick={toggle}
          className={cn('message-global--open-button')}
          aria-label={isOpen ? labels.hide : labels.show}
          aria-expanded={isOpen}
          aria-controls={id}
        >
          <div className="message-global--open-button-title">
            <IconWarning className="message-global--icon-warning" theme={theme} />
            <Heading
              className={cn('message-global--title', {
                'message-global--title--multi-lined': isMultiLined
              })}
              ref={titleRef}
              level={3}
            >
              {title}
            </Heading>
          </div>
          {isExpandable && (
            <div className="message-global--open-button-label">
              <span>{isOpen ? labels.hide : labels.show}</span>
              <Icon
                className={cn('message-global--icon-accordion', {
                  'message-global--icon-accordion--is-open': isOpen
                })}
                name="small-arrow-down"
                fill
              />
            </div>
          )}
        </Button>

        {isExpandable && (
          <Collapse
            className={cn('message-global--collapse', {
              'message-global--collapse--use-intrinsic-height': messageHeight || !isOpen
            })}
            isOpen={isOpen}
            onMeasure={(h: number) => {
              if (!messageHeight && isOpen) {
                setMessageHeight(h);
              }
            }}
          >
            <div className="message-global--collapse-inner" style={{ height: messageHeight || '100%' }}>
              {message && <HtmlString {...message} />}
              {length > 1 && (
                <div className="message-global--navigation">
                  <button onClick={() => setActiveIndex(index === 0 ? length - 1 : index - 1)}>
                    <Icon name="navigation-arrow" />
                    <span>{index + 1}</span>
                  </button>
                  <span className="message-global--navigation-of">{labels.of}</span>

                  <button onClick={() => setActiveIndex((index + 1) % length)}>
                    <span>{length}</span>
                    <Icon name="navigation-arrow" />
                  </button>
                </div>
              )}
            </div>
          </Collapse>
        )}
      </ContentContainer>
    </div>
  );
};

MessageGlobal.displayName = 'MessageGlobal';

export default MessageGlobal;
